import React, { useState, useEffect, useCallback } from 'react';
import RaceTrack from './RaceTrack';
import './DiglettDash.css';

const DiglettDashGame = ({ onClose, onGameComplete, entryFee = 50, isModal = false }) => {
  const [gameState, setGameState] = useState('waiting'); // waiting, starting, racing, finished
  const [raceData, setRaceData] = useState({
    racers: [],
    status: 'Ready to Race!'
  });
  const [countdown, setCountdown] = useState(3);
  const [raceResults, setRaceResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [playerSpeed, setPlayerSpeed] = useState(0);
  const [raceInterval, setRaceInterval] = useState(null);
  const [lastMoveTime, setLastMoveTime] = useState(0);
  const [raceId, setRaceId] = useState(null);
  const [raceStartTime, setRaceStartTime] = useState(null);
  const [raceDuration, setRaceDuration] = useState(0);
  const [finishOrder, setFinishOrder] = useState([]);
  const [finishTimes, setFinishTimes] = useState({});

  // Player controls with throttling for better mobile performance
  const handlePlayerMove = useCallback(() => {
    const now = Date.now();
    // Throttle to prevent spam clicking (minimum 50ms between moves)
    if (gameState === 'racing' && now - lastMoveTime > 50) {
      setLastMoveTime(now);
      setRaceData(prev => {
        const newRacers = prev.racers.map((racer, index) => {
          if (index === 0) {
            // Player racer - boost position by 3%
            return {
              ...racer,
              position: Math.min(100, racer.position + 3)
            };
          }
          return racer;
        });
        return { ...prev, racers: newRacers };
      });
    }
  }, [gameState, lastMoveTime]);

  // Keyboard controls - prevent holding down keys
  useEffect(() => {
    const pressedKeys = new Set();

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowUp' && gameState === 'racing' && !pressedKeys.has('ArrowUp')) {
        pressedKeys.add('ArrowUp');
        handlePlayerMove();
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === 'ArrowUp') {
        pressedKeys.delete('ArrowUp');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState, handlePlayerMove]);

  // Start race function
  const startRace = () => {
    setGameState('starting');
    setError('');

    // Create race data with racers (no backend call)
    const raceData = {
      race_id: Math.floor(Math.random() * 10000),
      racers: [
        { name: 'You', position: 0, color: '#FF6B35' },
        { name: 'Diglett 1', position: 0, color: '#8B4513' },
        { name: 'Diglett 2', position: 0, color: '#A0522D' },
        { name: 'Diglett 3', position: 0, color: '#CD853F' }
      ],
      status: 'Get Ready!'
    };

    setRaceData(raceData);
    setRaceId(raceData.race_id);

    // Start countdown
    let countdownValue = 3;
    setCountdown(countdownValue);

    const countdownInterval = setInterval(() => {
      countdownValue--;
      setCountdown(countdownValue);

      if (countdownValue <= 0) {
        clearInterval(countdownInterval);
        setGameState('racing');
        setRaceStartTime(Date.now()); // Record race start time
        startRaceAnimation();
      }
    }, 1000);
  };

  // Simulate race animation
  const startRaceAnimation = () => {
    const interval = setInterval(() => {
      setRaceData(prev => {
        const newRacers = prev.racers.map((racer, index) => {
          let newPosition;

          if (index === 0) {
            // Player controlled - position is already updated by handlePlayerMove
            newPosition = racer.position;
          } else {
            // NPC racers - random progress
            const speedVariation = Math.random() * 1.5 + 0.8; // 0.8 to 2.3% per update
            newPosition = Math.min(100, racer.position + speedVariation);
          }

          return {
            ...racer,
            position: newPosition,
            animationState: newPosition >= 95 ? 'finished' : 'underground'
          };
        });

        // Track racers who just finished (reached 100%)
        const currentTime = Date.now();

        // Track finish order based on who crosses 100% first
        const newFinishers = [];
        newRacers.forEach(racer => {
          if (racer.position >= 100 && !racer.finished) {
            racer.finished = true;
            newFinishers.push(racer);
          }
        });

        // Add new finishers to the finish order
        newFinishers.forEach(racer => {
          setFinishOrder(prev => [...prev, racer.name]);
        });

        // Check if ALL racers have finished (reached 100%)
        const allFinished = newRacers.every(racer => racer.position >= 100);
        if (allFinished) {
          clearInterval(interval);

          // Wait for state to update, then create results based on finish order
          setTimeout(() => {
            setFinishOrder(currentFinishOrder => {
              // Generate times based on finish order position
              const raceResults = {
                rankings: currentFinishOrder.map((racerName, index) => {
                  const baseTime = 8 + (index * 1.5) + (Math.random() * 1); // Faster finishers get better times
                  return {
                    position: index + 1,
                    name: racerName,
                    time: baseTime.toFixed(2),
                    isPlayer: racerName === 'You'
                  };
                }),
                playerPosition: currentFinishOrder.indexOf('You') + 1,
                totalDuration: currentTime - raceStartTime
              };

              setGameState('finished');
              setRaceResults(raceResults);

              if (onGameComplete) {
                const playerWon = raceResults.playerPosition === 1;
                onGameComplete(playerWon, raceResults.playerPosition);
              }

              return currentFinishOrder;
            });
          }, 100);

          return {
            ...prev,
            racers: newRacers,
            status: 'Race Complete!'
          };
        }

        return {
          ...prev,
          racers: newRacers,
          status: 'Racing!'
        };
      });
    }, 100);

    setRaceInterval(interval);
  };

  // Finish race and get results
  const finishRace = async () => {
    if (!raceId || !raceStartTime) return;

    // Calculate race duration
    const endTime = Date.now();
    const duration = endTime - raceStartTime;
    setRaceDuration(duration);

    // Determine final position based on player progress
    const playerRacer = raceData.racers[0];
    const playerProgress = playerRacer ? playerRacer.position : 0;

    // Calculate final position based on progress
    let finalPosition = 4; // Default to last place
    if (playerProgress >= 95) finalPosition = 1;
    else if (playerProgress >= 85) finalPosition = 2;
    else if (playerProgress >= 75) finalPosition = 3;

    try {
      const response = await fetch(`/v1/diglett-dash/finish-race`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` 
        },
        body: JSON.stringify({
          race_id: raceId,
          race_duration_ms: duration,
          final_position: finalPosition,
          player_progress: playerProgress
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to finish race');
      }

      const data = await response.json();
      setRaceResults(data);
      setGameState('finished');
      setRaceData(prev => ({ 
        ...prev, 
        status: `Race Complete! You finished ${data.final_position}${getOrdinalSuffix(data.final_position)}!`
      }));
    } catch (err) {
      console.error('Error finishing race:', err);
      setError(err.message || 'Failed to finish race');
      // Still show local results even if backend fails
      setRaceResults({
        race_completed: true,
        final_position: finalPosition,
        race_duration_ms: duration,
        prize_amount: 0
      });
      setGameState('finished');
    }
  };

  // Helper function for ordinal numbers
  const getOrdinalSuffix = (num) => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
  };

  // Reset race
  const resetRace = () => {
    setGameState('waiting');
    setRaceData({
      racers: [
        { id: 'player', position: 0, isPlayer: true, animationState: 'underground' },
        { id: 'npc1', position: 0, isPlayer: false, animationState: 'underground' },
        { id: 'npc2', position: 0, isPlayer: false, animationState: 'underground' },
        { id: 'npc3', position: 0, isPlayer: false, animationState: 'underground' }
      ],
      status: 'Ready to Race!'
    });
    setRaceId(null);
    setCountdown(3);
    setRaceResults(null);
    setError(null);
  };


  return (
    <div className={isModal ? "" : "diglett-dash-overlay"} onClick={isModal ? undefined : onClose}>
      <div className={isModal ? "" : "diglett-dash-container"} onClick={e => e.stopPropagation()}>
        {/* Race Track */}
        <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
          <RaceTrack raceData={raceData} trackWidth={100} trackHeight={500} />
        </div>

        {/* Controls */}
        <div className="race-controls">
          {gameState === 'waiting' && (
            <button 
              onClick={startRace}
              className="start-race-btn"
            >
              Start Race!
            </button>
          )}

          {gameState === 'racing' && (
            <button 
              onClick={(e) => {
                e.preventDefault();
                handlePlayerMove();
              }}
              className="move-btn"
              style={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #FF8E53 100%)',
                color: 'white',
                border: 'none',
                padding: '20px 40px',
                fontSize: '20px',
                fontWeight: 'bold',
                borderRadius: '12px',
                cursor: 'pointer',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                marginTop: '10px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                outline: 'none',
                WebkitTouchCallout: 'none'
              }}
            >
              ⬆️ MOVE UP!
            </button>
          )}

          {gameState === 'finished' && raceResults && (
            <div style={{
              background: 'rgba(0,0,0,0.9)',
              color: '#fff',
              padding: '25px',
              borderRadius: '15px',
              textAlign: 'center',
              marginTop: '20px',
              maxWidth: '400px',
              margin: '20px auto'
            }}>
              <h2 style={{ 
                color: '#FFD700', 
                marginBottom: '20px',
                fontSize: '24px',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}>
                🏁 Race Results
              </h2>

              <div style={{ marginBottom: '20px' }}>
                {raceResults.rankings.map((racer, index) => {
                  const positionColors = ['#FFD700', '#C0C0C0', '#CD7F32', '#8B4513'];
                  const positionEmojis = ['🥇', '🥈', '🥉', '4️⃣'];
                  const positionNames = ['First', 'Second', 'Third', 'Fourth'];

                  return (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 15px',
                      margin: '8px 0',
                      background: racer.isPlayer ? 'rgba(255, 107, 53, 0.3)' : 'rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      border: racer.isPlayer ? '2px solid #FF6B35' : '1px solid rgba(255,255,255,0.2)',
                      fontWeight: racer.isPlayer ? 'bold' : 'normal'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>{positionEmojis[index]}</span>
                        <span style={{ color: positionColors[index], fontWeight: 'bold' }}>
                          {positionNames[index]}
                        </span>
                        <span style={{ color: racer.isPlayer ? '#FF6B35' : '#fff' }}>
                          {racer.name}
                        </span>
                      </div>
                      <div style={{ 
                        color: '#FFD700',
                        fontFamily: 'monospace',
                        fontSize: '14px'
                      }}>
                        {racer.time}s
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button 
                  onClick={() => {
                    setGameState('waiting');
                    setRaceResults(null);
                    setPlayerSpeed(0);
                    setRaceData({ racers: [], status: 'Ready to Race!' });
                    setRaceStartTime(null);
                    setRaceDuration(0);
                    setFinishOrder([]);
                    setFinishTimes({});
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  🎮 Play Again
                </button>

                {isModal && (
                  <button 
                    onClick={onClose}
                    style={{
                      background: 'linear-gradient(135deg, #666 0%, #888 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                    }}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            color: '#ff4444',
            textAlign: 'center',
            marginTop: '10px',
            padding: '10px',
            background: 'rgba(255, 68, 68, 0.1)',
            borderRadius: '6px',
            border: '1px solid #ff4444'
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiglettDashGame;